import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { RefreshIcon, CheckIcon } from '../components/common/Icons';
import { useFeatureFlagOptional } from '../hooks/useFeatureFlag';
import { UpgradePrompt } from '../components/common/FeatureGate';

/**
 * TenantSettingsPage - Tenant Self-Service Settings
 *
 * Allows tenant admins to customize their instance's branding
 * and contact information.
 *
 * Phase 3.6.1: Tenant Settings Page
 */

interface BrandingSettings {
  platform_name: string;
  primary_color: string;
  secondary_color: string;
  accent_color?: string;
  background_style: 'solid' | 'gradient' | 'pattern';
  logo_url?: string;
}

interface ContactSettings {
  support_email?: string;
  support_url?: string;
  documentation_url?: string;
}

interface TenantSettings {
  tenant_id: string;
  tenant_name: string;
  branding: BrandingSettings;
  contact: ContactSettings;
  locked_fields: string[];
  updated_at: string;
}

const TenantSettingsPage: React.FC = () => {
  const { authFetch, isLoading: authLoading } = useAuth();
  const hasCustomBranding = useFeatureFlagOptional('customBranding');

  const [settings, setSettings] = useState<TenantSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Form state
  const [branding, setBranding] = useState<BrandingSettings>({
    platform_name: '',
    primary_color: '#3B82F6',
    secondary_color: '#1E40AF',
    background_style: 'solid',
  });
  const [contact, setContact] = useState<ContactSettings>({});

  // Load settings on mount
  const loadSettings = useCallback(async () => {
    if (authLoading) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await authFetch('/api/tenant-portal/settings');
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to load settings');
      }

      const data: TenantSettings = await response.json();
      setSettings(data);
      setBranding(data.branding);
      setContact(data.contact);
    } catch (err: any) {
      setError(err.message || 'Failed to load settings');
    } finally {
      setIsLoading(false);
    }
  }, [authFetch, authLoading]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  // Save settings
  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    setSaveSuccess(false);

    try {
      const response = await authFetch('/api/tenant-portal/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ branding, contact }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to save settings');
      }

      const data: TenantSettings = await response.json();
      setSettings(data);
      setBranding(data.branding);
      setContact(data.contact);
      setHasChanges(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  // Reset to defaults
  const handleReset = async () => {
    if (!window.confirm('Reset all branding settings to platform defaults?')) {
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const response = await authFetch('/api/tenant-portal/settings/reset', {
        method: 'POST',
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to reset settings');
      }

      const data: TenantSettings = await response.json();
      setSettings(data);
      setBranding(data.branding);
      setHasChanges(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to reset settings');
    } finally {
      setIsSaving(false);
    }
  };

  // Helper to check if field is locked
  const isFieldLocked = (field: string) =>
    settings?.locked_fields.includes(field) ?? false;

  // Update branding with change tracking
  const updateBranding = <K extends keyof BrandingSettings>(
    field: K,
    value: BrandingSettings[K]
  ) => {
    setBranding((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
    setError(null);
  };

  // Update contact with change tracking
  const updateContact = <K extends keyof ContactSettings>(
    field: K,
    value: ContactSettings[K]
  ) => {
    setContact((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
    setError(null);
  };

  if (isLoading) {
    return (
      <div className="page-container">
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '400px',
            color: 'var(--text-secondary)',
          }}
        >
          <RefreshIcon size={24} style={{ animation: 'spin 1s linear infinite' }} />
          <span style={{ marginLeft: 'var(--space-3)' }}>Loading settings...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Page Header */}
      <div
        style={{
          marginBottom: 'var(--space-8)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}
      >
        <div>
          <h1
            style={{
              fontSize: 'var(--text-2xl)',
              fontWeight: 600,
              marginBottom: 'var(--space-2)',
            }}
          >
            Organization Settings
          </h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)' }}>
            Customize your workspace appearance and contact information
          </p>
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
          <button
            className="btn btn-secondary"
            onClick={handleReset}
            disabled={isSaving}
          >
            Reset to Defaults
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
          >
            {isSaving ? (
              <>
                <RefreshIcon size={16} style={{ animation: 'spin 1s linear infinite' }} />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </button>
        </div>
      </div>

      {/* Status Messages */}
      {error && (
        <div
          style={{
            padding: 'var(--space-4)',
            marginBottom: 'var(--space-6)',
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: 'var(--radius-lg)',
            color: 'var(--color-error)',
          }}
        >
          {error}
        </div>
      )}

      {saveSuccess && (
        <div
          style={{
            padding: 'var(--space-4)',
            marginBottom: 'var(--space-6)',
            background: 'rgba(34, 197, 94, 0.1)',
            border: '1px solid rgba(34, 197, 94, 0.3)',
            borderRadius: 'var(--radius-lg)',
            color: 'var(--color-success)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
          }}
        >
          <CheckIcon size={16} />
          Settings saved successfully
        </div>
      )}

      <div style={{ display: 'grid', gap: 'var(--space-6)' }}>
        {/* Branding Section - gated by customBranding feature flag */}
        {hasCustomBranding ? (
        <div
          className="card"
          style={{
            background: 'var(--glass-bg)',
            border: '1px solid var(--glass-border)',
            borderRadius: 'var(--radius-xl)',
            padding: 'var(--space-6)',
          }}
        >
          <h2
            style={{
              fontSize: 'var(--text-lg)',
              fontWeight: 600,
              marginBottom: 'var(--space-6)',
            }}
          >
            Branding
          </h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
            {/* Platform Name */}
            <div className="input-group">
              <label className="input-label">
                Platform Name
                {isFieldLocked('platform_name') && (
                  <span
                    style={{
                      marginLeft: 'var(--space-2)',
                      color: 'var(--text-tertiary)',
                      fontSize: 'var(--text-xs)',
                    }}
                  >
                    (locked by administrator)
                  </span>
                )}
              </label>
              <input
                type="text"
                className="input"
                value={branding.platform_name}
                onChange={(e) => updateBranding('platform_name', e.target.value)}
                disabled={isFieldLocked('platform_name')}
                placeholder="Your Organization Name"
              />
            </div>

            {/* Colors Row */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 'var(--space-4)' }}>
              {/* Primary Color */}
              <div className="input-group">
                <label className="input-label">
                  Primary Color
                  {isFieldLocked('primary_color') && (
                    <span
                      style={{
                        marginLeft: 'var(--space-2)',
                        color: 'var(--text-tertiary)',
                        fontSize: 'var(--text-xs)',
                      }}
                    >
                      (locked)
                    </span>
                  )}
                </label>
                <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                  <input
                    type="color"
                    value={branding.primary_color}
                    onChange={(e) => updateBranding('primary_color', e.target.value.toUpperCase())}
                    disabled={isFieldLocked('primary_color')}
                    style={{
                      width: '48px',
                      height: '40px',
                      padding: '4px',
                      borderRadius: 'var(--radius-md)',
                      border: '1px solid var(--border-color)',
                      cursor: isFieldLocked('primary_color') ? 'not-allowed' : 'pointer',
                    }}
                  />
                  <input
                    type="text"
                    className="input"
                    value={branding.primary_color}
                    onChange={(e) => updateBranding('primary_color', e.target.value.toUpperCase())}
                    disabled={isFieldLocked('primary_color')}
                    style={{ flex: 1 }}
                    placeholder="#3B82F6"
                  />
                </div>
              </div>

              {/* Secondary Color */}
              <div className="input-group">
                <label className="input-label">
                  Secondary Color
                  {isFieldLocked('secondary_color') && (
                    <span
                      style={{
                        marginLeft: 'var(--space-2)',
                        color: 'var(--text-tertiary)',
                        fontSize: 'var(--text-xs)',
                      }}
                    >
                      (locked)
                    </span>
                  )}
                </label>
                <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                  <input
                    type="color"
                    value={branding.secondary_color}
                    onChange={(e) => updateBranding('secondary_color', e.target.value.toUpperCase())}
                    disabled={isFieldLocked('secondary_color')}
                    style={{
                      width: '48px',
                      height: '40px',
                      padding: '4px',
                      borderRadius: 'var(--radius-md)',
                      border: '1px solid var(--border-color)',
                      cursor: isFieldLocked('secondary_color') ? 'not-allowed' : 'pointer',
                    }}
                  />
                  <input
                    type="text"
                    className="input"
                    value={branding.secondary_color}
                    onChange={(e) => updateBranding('secondary_color', e.target.value.toUpperCase())}
                    disabled={isFieldLocked('secondary_color')}
                    style={{ flex: 1 }}
                    placeholder="#1E40AF"
                  />
                </div>
              </div>

              {/* Accent Color */}
              <div className="input-group">
                <label className="input-label">Accent Color (Optional)</label>
                <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                  <input
                    type="color"
                    value={branding.accent_color || '#10B981'}
                    onChange={(e) => updateBranding('accent_color', e.target.value.toUpperCase())}
                    style={{
                      width: '48px',
                      height: '40px',
                      padding: '4px',
                      borderRadius: 'var(--radius-md)',
                      border: '1px solid var(--border-color)',
                      cursor: 'pointer',
                    }}
                  />
                  <input
                    type="text"
                    className="input"
                    value={branding.accent_color || ''}
                    onChange={(e) => updateBranding('accent_color', e.target.value.toUpperCase() || undefined)}
                    style={{ flex: 1 }}
                    placeholder="#10B981"
                  />
                </div>
              </div>
            </div>

            {/* Background Style */}
            <div className="input-group">
              <label className="input-label">Background Style</label>
              <select
                className="input"
                value={branding.background_style}
                onChange={(e) =>
                  updateBranding('background_style', e.target.value as BrandingSettings['background_style'])
                }
              >
                <option value="solid">Solid Color</option>
                <option value="gradient">Gradient</option>
                <option value="pattern">Pattern</option>
              </select>
            </div>

            {/* Live Preview */}
            <div>
              <label className="input-label" style={{ marginBottom: 'var(--space-3)' }}>
                Preview
              </label>
              <div
                style={{
                  padding: 'var(--space-4)',
                  borderRadius: 'var(--radius-lg)',
                  border: '1px solid var(--border-color)',
                  background:
                    branding.background_style === 'gradient'
                      ? `linear-gradient(135deg, ${branding.primary_color} 0%, ${branding.secondary_color} 100%)`
                      : branding.background_style === 'pattern'
                      ? `repeating-linear-gradient(45deg, ${branding.primary_color}22, ${branding.primary_color}22 10px, transparent 10px, transparent 20px)`
                      : 'var(--surface-1)',
                }}
              >
                <div
                  style={{
                    padding: 'var(--space-4)',
                    background: branding.primary_color,
                    borderRadius: 'var(--radius-md)',
                    color: 'white',
                    fontWeight: 600,
                    marginBottom: 'var(--space-3)',
                  }}
                >
                  {branding.platform_name || 'Your Platform'}
                </div>
                <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
                  <button
                    style={{
                      padding: 'var(--space-2) var(--space-4)',
                      background: branding.secondary_color,
                      color: 'white',
                      border: 'none',
                      borderRadius: 'var(--radius-md)',
                      cursor: 'pointer',
                    }}
                  >
                    Primary Button
                  </button>
                  {branding.accent_color && (
                    <button
                      style={{
                        padding: 'var(--space-2) var(--space-4)',
                        background: branding.accent_color,
                        color: 'white',
                        border: 'none',
                        borderRadius: 'var(--radius-md)',
                        cursor: 'pointer',
                      }}
                    >
                      Accent Button
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        ) : (
          <UpgradePrompt feature="customBranding" compact />
        )}

        {/* Contact Section */}
        <div
          className="card"
          style={{
            background: 'var(--glass-bg)',
            border: '1px solid var(--glass-border)',
            borderRadius: 'var(--radius-xl)',
            padding: 'var(--space-6)',
          }}
        >
          <h2
            style={{
              fontSize: 'var(--text-lg)',
              fontWeight: 600,
              marginBottom: 'var(--space-6)',
            }}
          >
            Support & Contact
          </h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
            {/* Support Email */}
            <div className="input-group">
              <label className="input-label">Support Email</label>
              <input
                type="email"
                className="input"
                value={contact.support_email || ''}
                onChange={(e) => updateContact('support_email', e.target.value || undefined)}
                placeholder="support@yourcompany.com"
              />
              <p
                style={{
                  fontSize: 'var(--text-xs)',
                  color: 'var(--text-tertiary)',
                  marginTop: 'var(--space-1)',
                }}
              >
                Users can reach your support team at this email
              </p>
            </div>

            {/* Support URL */}
            <div className="input-group">
              <label className="input-label">Support Portal URL</label>
              <input
                type="url"
                className="input"
                value={contact.support_url || ''}
                onChange={(e) => updateContact('support_url', e.target.value || undefined)}
                placeholder="https://support.yourcompany.com"
              />
            </div>

            {/* Documentation URL */}
            <div className="input-group">
              <label className="input-label">Documentation URL</label>
              <input
                type="url"
                className="input"
                value={contact.documentation_url || ''}
                onChange={(e) => updateContact('documentation_url', e.target.value || undefined)}
                placeholder="https://docs.yourcompany.com"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Footer with Last Updated */}
      {settings && (
        <div
          style={{
            marginTop: 'var(--space-6)',
            padding: 'var(--space-4)',
            textAlign: 'center',
            color: 'var(--text-tertiary)',
            fontSize: 'var(--text-xs)',
          }}
        >
          Last updated: {new Date(settings.updated_at).toLocaleString()}
        </div>
      )}
    </div>
  );
};

export default TenantSettingsPage;
